import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import PlacesAutocompleteInput from '../ui/places-autocomplete-input';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Lock,
  Loader2,
  Wand2,
  MapPin,
  ChevronRight,
  RefreshCw,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { useCompanyTechnicians } from '../../hooks/useCompanyTechnicians';
import { useCompanyDispatchDefaults } from '../../hooks/useCompanyDispatchDefaults';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import { useRole } from '../../context/RoleContext';
import { isClosedAccount } from '../../utils/accountLifecycle';
import { isInsidePacContainer } from '../../lib/googleMapsLoader';

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  capacity_exceeded: 'Over capacity',
  missing_address: 'No address on file',
  geocode_failed: 'Could not route',
  appointment: 'Locked appointment',
};

const REASON_TONE = {
  capacity_exceeded: 'bg-amber-50 text-amber-800 border-amber-200',
  missing_address: 'bg-red-50 text-red-700 border-red-200',
  geocode_failed: 'bg-red-50 text-red-700 border-red-200',
  appointment: 'bg-gray-100 text-gray-600 border-gray-200',
};

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * BuildDaySheet — preview-first multi-tech route planner.
 *
 * Stage 1 (Configure): operator picks techs, start address, max stops.
 * Stage 2 (Preview):   server-returned plan, accept/modify/cancel.
 *
 * Boundaries (locked):
 *   • Never auto-applies — Apply is an explicit click.
 *   • Default touches ONLY the Unassigned column. Reassign toggle is OFF
 *     by default and must be explicitly opted into.
 *   • Appointment-mode jobs are returned as `preservedLocked` and never
 *     written to.
 *   • Stale-preview detection via inputDigest → 409 surfaces Regenerate.
 */
const BuildDaySheet = ({ open, onOpenChange, dispatchDate, companyId, onSendReminders }) => {
  const { tasks } = useTasks();
  const { leads } = useLeads();
  const leadsMap = useMemo(
    () => Object.fromEntries((leads || []).map((l) => [l.id, l])),
    [leads]
  );
  const { technicians } = useCompanyTechnicians(companyId);
  const { defaults: companyDefaults } = useCompanyDispatchDefaults(companyId);
  const { role } = useRole();
  const isAdmin = ['owner', 'admin'].includes((role || '').toLowerCase());

  // ── State ────────────────────────────────────────────────────────────────
  const [stage, setStage] = useState('configure'); // 'configure' | 'preview'
  const [selected, setSelected] = useState({});     // uid → { checked, startAddress, maxStops, halfDay }
  const [reassign, setReassign] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);

  // ── Resolve effective defaults per tech (fallback chain) ────────────────
  // tech.defaultStartAddress  → company.companyDefaultDepot     → ''
  // tech.defaultMaxStops      → company.companyDefaultMaxStops  → 6
  // tech.defaultHalfDay       → false
  // tech.endLocationMode      → company.companyDefaultEndLocationMode → 'office'
  // tech.defaultEndAddress    → tech.defaultHomeAddress (if mode=='home') →
  //                             company.companyDefaultEndDepot → start (if mode=='office') → ''
  //
  // Each field also exposes its `source` so the UI can render a pill telling
  // the operator where the value came from. Sources: 'tech' | 'company' |
  // 'derived' (computed from another resolved field) | 'none'.
  const resolvedDefaults = useMemo(() => {
    const m = {};
    technicians.forEach((t) => {
      const startSource =
        t.defaultStartAddress ? 'tech'
        : (companyDefaults.companyDefaultDepot ? 'company' : 'none');
      const startValue =
        t.defaultStartAddress
        || companyDefaults.companyDefaultDepot
        || '';
      const maxStopsSource =
        typeof t.defaultMaxStops === 'number' ? 'tech'
        : (typeof companyDefaults.companyDefaultMaxStops === 'number' ? 'company' : 'none');
      const maxStopsValue =
        typeof t.defaultMaxStops === 'number' ? t.defaultMaxStops
        : (companyDefaults.companyDefaultMaxStops ?? 6);

      // End-location resolution
      const endModeRaw = (t.endLocationMode
        || companyDefaults.companyDefaultEndLocationMode
        || 'office').toLowerCase();
      const endMode = ['office','home','custom','none'].includes(endModeRaw) ? endModeRaw : 'office';
      const endModeSource =
        t.endLocationMode ? 'tech'
        : (companyDefaults.companyDefaultEndLocationMode ? 'company' : 'derived');
      let endAddressValue = '';
      let endAddressSource = 'derived';
      if (endMode === 'none') {
        endAddressValue = '';
        endAddressSource = 'derived';
      } else if (endMode === 'home') {
        if (t.defaultHomeAddress)      { endAddressValue = t.defaultHomeAddress; endAddressSource = 'tech'; }
        else if (t.defaultEndAddress)  { endAddressValue = t.defaultEndAddress;  endAddressSource = 'tech'; }
        else                           { endAddressValue = ''; endAddressSource = 'none'; }
      } else if (endMode === 'custom') {
        if (t.defaultEndAddress)                       { endAddressValue = t.defaultEndAddress; endAddressSource = 'tech'; }
        else if (companyDefaults.companyDefaultEndDepot) { endAddressValue = companyDefaults.companyDefaultEndDepot; endAddressSource = 'company'; }
        else                                            { endAddressValue = ''; endAddressSource = 'none'; }
      } else {
        // office → falls back to startValue. Stays "derived" so the UI
        // shows "(uses Start)" rather than a misleading source pill.
        endAddressValue = startValue;
        endAddressSource = 'derived';
      }

      m[t.uid] = {
        startAddress: startValue,
        startAddressSource: startSource,
        maxStops: maxStopsValue,
        maxStopsSource: maxStopsSource,
        halfDay: !!t.defaultHalfDay,
        halfDaySource: typeof t.defaultHalfDay === 'boolean' ? 'tech' : 'none',
        endLocationMode: endMode,
        endLocationModeSource: endModeSource,
        endAddress: endAddressValue,
        endAddressSource: endAddressSource,
      };
    });
    return m;
  }, [technicians, companyDefaults]);

  // ── Initialize tech rows when sheet opens ────────────────────────────────
  /* eslint-disable react-hooks/set-state-in-effect -- Open-modal reset: re-initialize the form state whenever the sheet opens. */
  useEffect(() => {
    if (!open) return;
    setStage('configure');
    setError(null);
    setPreview(null);
    setReassign(false);
    setSelected(() => {
      const next = {};
      technicians.forEach((t) => {
        const def = resolvedDefaults[t.uid] || { startAddress: '', maxStops: 6, halfDay: false, endLocationMode: 'office', endAddress: '' };
        next[t.uid] = {
          checked: false,
          startAddress: def.startAddress,
          maxStops: def.maxStops,
          halfDay: def.halfDay,
          endLocationMode: def.endLocationMode,
          endAddress: def.endAddress,
        };
      });
      return next;
    });
  }, [open, technicians, resolvedDefaults]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // ── Override detection per row ───────────────────────────────────────────
  // A field is "overridden" iff its current sheet value differs from the
  // resolved default. Used to render the amber Override pill and to gate the
  // "Save as defaults" affordance.
  const overrideMap = useMemo(() => {
    const m = {};
    technicians.forEach((t) => {
      const row = selected[t.uid] || {};
      const def = resolvedDefaults[t.uid] || {};
      const ms = parseInt(row.maxStops, 10);
      m[t.uid] = {
        startAddress: (row.startAddress || '').trim() !== (def.startAddress || '').trim(),
        maxStops: !Number.isNaN(ms) && ms !== def.maxStops,
        halfDay: !!row.halfDay !== !!def.halfDay,
        endLocationMode: (row.endLocationMode || 'office') !== (def.endLocationMode || 'office'),
        endAddress: (row.endAddress || '').trim() !== (def.endAddress || '').trim(),
      };
    });
    return m;
  }, [technicians, selected, resolvedDefaults]);

  const anyOverridden = useMemo(
    () => Object.values(overrideMap).some((r) => r.startAddress || r.maxStops || r.halfDay || r.endLocationMode || r.endAddress),
    [overrideMap]
  );

  // ── Job counts derived from current TasksContext state ───────────────────
  const dateKey = useMemo(() => {
    if (!dispatchDate) return '';
    const y = dispatchDate.getFullYear();
    const m = String(dispatchDate.getMonth() + 1).padStart(2, '0');
    const d = String(dispatchDate.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [dispatchDate]);

  const jobsOnDate = useMemo(() => {
    if (!dispatchDate) return [];
    return tasks.filter((t) => {
      if (t.type !== 'job') return false;
      if (!t.scheduledStart) return false;
      const d = new Date(t.scheduledStart);
      if (Number.isNaN(d.getTime())) return false;
      return (
        d.getFullYear() === dispatchDate.getFullYear() &&
        d.getMonth() === dispatchDate.getMonth() &&
        d.getDate() === dispatchDate.getDate() &&
        (t.status || '') !== 'completed' &&
        (t.status || '') !== 'cancelled' &&
        !isClosedAccount(leadsMap[t.leadId])
      );
    });
  }, [tasks, dispatchDate, leadsMap]);

  const jobBuckets = useMemo(() => {
    const unassigned = [];
    const assignedRoute = [];
    const locked = [];
    jobsOnDate.forEach((t) => {
      const mode = (t.jobMode || 'route').toLowerCase();
      if (mode === 'appointment') locked.push(t);
      else if (!t.assignedTo) unassigned.push(t);
      else assignedRoute.push(t);
    });
    return { unassigned, assignedRoute, locked };
  }, [jobsOnDate]);

  const appointmentCountByTech = useMemo(() => {
    const m = {};
    jobBuckets.locked.forEach((t) => {
      if (t.assignedTo) m[t.assignedTo] = (m[t.assignedTo] || 0) + 1;
    });
    return m;
  }, [jobBuckets.locked]);

  // ── Generate handler ─────────────────────────────────────────────────────
  const activeTechCount = useMemo(
    () => technicians.filter((t) => selected[t.uid]?.checked).length,
    [technicians, selected]
  );

  // Live node-count indicator — operator sees scale before clicking Generate.
  // Matches backend MAX_NODES = 100 and SOFT_WARN_NODES = 75.
  const nodeCount = activeTechCount + jobBuckets.unassigned.length
    + (reassign ? jobBuckets.assignedRoute.length : 0);
  const aboveSoftWarn = nodeCount >= 75;
  const aboveHardCap = nodeCount > 100;
  // Rough plan-time estimate: 1 chunk ≈ 0.5s, parallel asyncio.gather.
  // ceil(N/25)^2 chunks; sequential floor of ~0.5s; parallel keeps total close to single chunk.
  const planSecondsEstimate = nodeCount === 0
    ? 0
    : Math.max(1, Math.ceil(nodeCount / 25) * 0.4);

  const canGenerate = useMemo(() => {
    const activeTechs = technicians.filter((t) => selected[t.uid]?.checked);
    if (activeTechs.length === 0) return false;
    if (aboveHardCap) return false;
    return activeTechs.every((t) => {
      const row = selected[t.uid] || {};
      if ((row.startAddress || '').trim().length === 0) return false;
      // 'home' and 'custom' modes must have a concrete end address. 'office'
      // and 'none' do not (office reuses start, none has no return leg).
      const mode = (row.endLocationMode || 'office').toLowerCase();
      if (mode === 'home' || mode === 'custom') {
        return (row.endAddress || '').trim().length > 0;
      }
      return true;
    });
  }, [technicians, selected, aboveHardCap]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const payload = {
        companyId,
        date: dateKey,
        reassignAlreadyAssigned: reassign,
        technicians: technicians
          .filter((t) => selected[t.uid]?.checked)
          .map((t) => {
            const row = selected[t.uid];
            const mode = (row.endLocationMode || 'office').toLowerCase();
            // For 'office' mode the backend already defaults endAddress to
            // startAddress when blank. For 'home'/'custom' we always send
            // the explicit endAddress. For 'none' we omit it entirely.
            const tech = {
              uid: t.uid,
              startAddress: (row.startAddress || '').trim(),
              maxStops: Math.max(1, parseInt(row.maxStops, 10) || 6),
              halfDay: !!row.halfDay,
              endLocationMode: mode,
            };
            if (mode === 'home' || mode === 'custom') {
              tech.endAddress = (row.endAddress || '').trim();
            } else if (mode === 'office') {
              tech.endAddress = (row.endAddress || row.startAddress || '').trim();
            }
            return tech;
          }),
      };
      const res = await axios.post(`${API}/api/dispatch/build-day`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreview(res.data);
      setStage('preview');
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Failed to generate plan';
      setError(detail);
      toast.error('Could not generate plan', { description: detail });
    } finally {
      setGenerating(false);
    }
  }, [companyId, dateKey, reassign, selected, technicians]);

  // ── Save-as-Defaults handler ─────────────────────────────────────────────
  // Persists each overridden row to its membership doc (per-tech defaults) and
  // optionally to the company doc if multiple techs share the same start
  // address override and the depot is empty. Pure additive: never auto-fires.
  const handleSaveDefaults = useCallback(async () => {
    if (!isAdmin || savingDefaults) return;
    setSavingDefaults(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Per-tech persistence — one PUT per overridden tech, only the fields
      // that actually differ from the current resolved default.
      const pending = [];
      let savedCount = 0;
      technicians.forEach((t) => {
        const row = selected[t.uid];
        const ov  = overrideMap[t.uid];
        if (!row || !ov) return;
        if (!ov.startAddress && !ov.maxStops && !ov.halfDay && !ov.endLocationMode && !ov.endAddress) return;
        const patch = {};
        if (ov.startAddress) patch.defaultStartAddress = (row.startAddress || '').trim();
        if (ov.maxStops)     patch.defaultMaxStops     = Math.max(1, parseInt(row.maxStops, 10) || 6);
        if (ov.halfDay)      patch.defaultHalfDay      = !!row.halfDay;
        if (ov.endLocationMode) patch.endLocationMode  = (row.endLocationMode || 'office').toLowerCase();
        if (ov.endAddress)   patch.defaultEndAddress   = (row.endAddress || '').trim();
        if (Object.keys(patch).length === 0) return;
        savedCount += 1;
        pending.push(
          axios.put(
            `${API}/api/team/${t.uid}/dispatch-defaults`,
            patch,
            { params: { companyId }, headers },
          ),
        );
      });

      await Promise.all(pending);
      toast.success(`Saved defaults for ${savedCount} technician${savedCount !== 1 ? 's' : ''}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Could not save defaults';
      toast.error('Save failed', { description: detail });
    } finally {
      setSavingDefaults(false);
    }
  }, [isAdmin, savingDefaults, technicians, selected, overrideMap, companyId]);

  // ── Apply handler ────────────────────────────────────────────────────────
  const handleApply = useCallback(async () => {
    if (!preview) return;
    setApplying(true);
    setError(null);
    try {
      const writes = [];
      preview.assignments.forEach((a) => {
        a.stops.forEach((s) => {
          if (s.isLocked) return;
          writes.push({
            taskId: s.taskId,
            assignedTo: a.techUid,
            assignedToName: a.techName,
            dispatchOrder: s.order * 100,
          });
        });
      });
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/dispatch/build-day/apply`,
        {
          companyId,
          previewId: preview.previewId,
          inputDigest: preview.inputDigest,
          writes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const count = res.data?.appliedTaskCount ?? writes.length;
      const already = res.data?.alreadyApplied;
      toast.success(
        already
          ? 'Plan already applied'
          : `Applied — ${count} job${count !== 1 ? 's' : ''} assigned`,
        {
          description: 'Routes optimized. Want to notify the techs?',
          action: onSendReminders
            ? { label: 'Send Reminders', onClick: () => onSendReminders() }
            : undefined,
          duration: 6000,
        }
      );
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message || 'Apply failed';
      if (status === 409) {
        setError('Plan is stale — one or more jobs changed after preview. Regenerate to continue.');
      } else {
        setError(detail);
      }
      toast.error('Could not apply plan', { description: detail });
    } finally {
      setApplying(false);
    }
  }, [preview, companyId, onOpenChange, onSendReminders]);

  // ── Render: Configure ────────────────────────────────────────────────────
  const renderConfigure = () => (
    <div className="space-y-5">
      {/* Working Today */}
      <section data-testid="bd-working-today-section">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Working today</h3>
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
          {technicians.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              No technicians on this company yet.
            </div>
          )}
          {technicians.map((t) => {
            const row = selected[t.uid] || {};
            const ov  = overrideMap[t.uid] || {};
            const def = resolvedDefaults[t.uid] || {};
            const apptCount = appointmentCountByTech[t.uid] || 0;
            return (
              <div key={t.uid} className="p-3 space-y-2" data-testid={`bd-tech-row-${t.uid}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={!!row.checked}
                    onCheckedChange={(v) =>
                      setSelected((s) => ({ ...s, [t.uid]: { ...s[t.uid], checked: !!v } }))
                    }
                    data-testid={`bd-tech-toggle-${t.uid}`}
                  />
                  <span className="text-sm font-medium text-gray-900 flex-1">{t.name}</span>
                  {apptCount > 0 && (
                    <span
                      className="text-[10px] text-gray-500"
                      title="Existing fixed appointments — capacity is in addition to these"
                    >
                      {apptCount} appt{apptCount !== 1 ? 's' : ''} already
                    </span>
                  )}
                </label>
                {row.checked && (
                  <div className="pl-7 space-y-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <PlacesAutocompleteInput
                          placeholder="Start address (e.g., 400 Main St, Stamford, CT)"
                          value={row.startAddress || ''}
                          onChange={(v) =>
                            setSelected((s) => ({
                              ...s,
                              [t.uid]: { ...s[t.uid], startAddress: v },
                            }))
                          }
                          className="h-8 text-xs"
                          data-testid={`bd-tech-address-${t.uid}`}
                        />
                        <SourcePill
                          source={def.startAddressSource}
                          overridden={ov.startAddress}
                          testId={`bd-address-pill-${t.uid}`}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <label className="flex items-center gap-2">
                        Max stops
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          value={row.maxStops ?? 6}
                          onChange={(e) =>
                            setSelected((s) => ({
                              ...s,
                              [t.uid]: { ...s[t.uid], maxStops: e.target.value },
                            }))
                          }
                          className="h-7 w-16 text-xs"
                          data-testid={`bd-tech-maxstops-${t.uid}`}
                        />
                        <SourcePill
                          source={def.maxStopsSource}
                          overridden={ov.maxStops}
                          testId={`bd-maxstops-pill-${t.uid}`}
                        />
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={!!row.halfDay}
                          onCheckedChange={(v) =>
                            setSelected((s) => ({
                              ...s,
                              [t.uid]: { ...s[t.uid], halfDay: !!v },
                            }))
                          }
                          data-testid={`bd-tech-halfday-${t.uid}`}
                        />
                        Half day
                        {ov.halfDay && (
                          <SourcePill
                            source={def.halfDaySource}
                            overridden
                            testId={`bd-halfday-pill-${t.uid}`}
                          />
                        )}
                      </label>
                    </div>

                    {/* ── End location row (Phase 1 end-location routing) ──
                        Lets dispatchers pick where today's route closes per
                        tech, independent of the saved default. Backend
                        accepts endLocationMode + endAddress and computes
                        the closing leg into total drive + mileage. */}
                    <div className="flex items-center gap-2 flex-wrap" data-testid={`bd-tech-end-row-${t.uid}`}>
                      <span className="text-[11px] font-semibold text-gray-700">End</span>
                      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
                        {[
                          { v: 'office', label: 'Office' },
                          { v: 'home',   label: 'Home' },
                          { v: 'custom', label: 'Custom' },
                          { v: 'none',   label: 'None' },
                        ].map((opt) => {
                          const active = (row.endLocationMode || 'office') === opt.v;
                          return (
                            <button
                              key={opt.v}
                              type="button"
                              onClick={() => {
                                setSelected((s) => {
                                  const cur = s[t.uid] || {};
                                  // When switching modes, re-derive endAddress from
                                  // the resolved default for the *new* mode if the
                                  // operator hasn't manually edited it for this run.
                                  const nextRow = { ...cur, endLocationMode: opt.v };
                                  const dx = resolvedDefaults[t.uid] || {};
                                  if (opt.v === 'office') {
                                    nextRow.endAddress = nextRow.startAddress || dx.startAddress || '';
                                  } else if (opt.v === 'none') {
                                    nextRow.endAddress = '';
                                  } else {
                                    // home / custom — only auto-fill if the saved
                                    // default for that mode is present.
                                    const presetEnd = dx.endLocationMode === opt.v ? dx.endAddress : '';
                                    nextRow.endAddress = presetEnd || nextRow.endAddress || '';
                                  }
                                  return { ...s, [t.uid]: nextRow };
                                });
                              }}
                              className={`h-6 px-2 rounded text-[10.5px] font-semibold transition-all ${
                                active
                                  ? 'bg-white shadow-sm text-gray-900'
                                  : 'text-gray-500 hover:text-gray-700'
                              }`}
                              data-testid={`bd-tech-endmode-${t.uid}-${opt.v}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <SourcePill
                        source={def.endLocationModeSource}
                        overridden={ov.endLocationMode}
                        testId={`bd-endmode-pill-${t.uid}`}
                      />
                      {/* Mode-aware helper / inputs */}
                      {(row.endLocationMode === 'office' || !row.endLocationMode) && (
                        <span className="text-[10px] text-gray-500 italic">
                          Returns to Start address
                        </span>
                      )}
                      {row.endLocationMode === 'none' && (
                        <span className="text-[10px] text-gray-500 italic">
                          Open route — no return leg counted
                        </span>
                      )}
                    </div>

                    {(row.endLocationMode === 'home' || row.endLocationMode === 'custom') && (
                      <div
                        className="flex items-center gap-2"
                        data-testid={`bd-tech-end-address-row-${t.uid}`}
                      >
                        <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <PlacesAutocompleteInput
                          placeholder={
                            row.endLocationMode === 'home'
                              ? "Technician's home address"
                              : "Custom end address (today's override)"
                          }
                          value={row.endAddress || ''}
                          onChange={(v) =>
                            setSelected((s) => ({
                              ...s,
                              [t.uid]: { ...s[t.uid], endAddress: v },
                            }))
                          }
                          className="h-8 text-xs"
                          data-testid={`bd-tech-end-address-${t.uid}`}
                        />
                        <SourcePill
                          source={def.endAddressSource}
                          overridden={ov.endAddress}
                          testId={`bd-end-address-pill-${t.uid}`}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* Save as Defaults — only shown when at least one field is overridden
            AND the operator has admin/owner permissions. Click is the ONLY
            way to persist; nothing is auto-saved. */}
        {anyOverridden && isAdmin && (
          <div
            className="mt-2 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
            data-testid="bd-save-defaults-banner"
          >
            <div className="text-xs text-amber-900 leading-tight">
              <div className="font-semibold">You changed some values for this run.</div>
              <div className="text-amber-700">
                These changes won&apos;t be saved unless you click Save as defaults.
              </div>
            </div>
            <button
              type="button"
              onClick={handleSaveDefaults}
              disabled={savingDefaults}
              className="h-7 px-3 text-[11px] font-semibold rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              data-testid="bd-save-defaults-btn"
            >
              {savingDefaults
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <CheckCircle2 className="h-3 w-3" />
              }
              {savingDefaults ? 'Saving…' : 'Save as defaults'}
            </button>
          </div>
        )}
      </section>

      {/* Jobs to plan */}
      <section data-testid="bd-jobs-section">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Jobs to plan</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600">Unassigned</span>
            <span className="font-medium text-gray-900">
              {jobBuckets.unassigned.length} job{jobBuckets.unassigned.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={reassign}
                onCheckedChange={(v) => setReassign(!!v)}
                data-testid="bd-reassign-toggle"
              />
              <span className="text-gray-600">
                Reassign already-assigned route jobs ({jobBuckets.assignedRoute.length})
              </span>
            </label>
          </div>
          <p className="text-[10px] text-gray-500 pl-6">
            Off = only the Unassigned column is planned. On = the system may move route jobs
            between technicians.
          </p>
          <div className="flex justify-between pt-1 border-t border-gray-200">
            <span className="text-gray-600 inline-flex items-center gap-1">
              <Lock className="h-3 w-3" /> Locked appointments
            </span>
            <span className="font-medium text-gray-500">
              {jobBuckets.locked.length} — never moved
            </span>
          </div>
        </div>
      </section>

      {error && (
        <div
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-2"
          data-testid="bd-config-error"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Live node-count indicator + soft/hard warnings */}
      {activeTechCount > 0 && (
        <div
          className={`text-[11px] rounded-md px-3 py-2 flex items-start gap-2 border ${
            aboveHardCap
              ? 'bg-red-50 border-red-200 text-red-800'
              : aboveSoftWarn
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-gray-50 border-gray-200 text-gray-600'
          }`}
          data-testid="bd-node-count"
        >
          <span className="font-medium">
            {jobBuckets.unassigned.length + (reassign ? jobBuckets.assignedRoute.length : 0)} jobs ×{' '}
            {activeTechCount} tech{activeTechCount !== 1 ? 's' : ''} = {nodeCount} nodes
          </span>
          {!aboveHardCap && nodeCount > 0 && (
            <span className="text-gray-500">· ~{planSecondsEstimate < 2 ? '1' : Math.ceil(planSecondsEstimate)}s plan time</span>
          )}
          {aboveSoftWarn && !aboveHardCap && (
            <span className="ml-1">
              · Large day — consider splitting into morning/afternoon passes for easier review.
            </span>
          )}
          {aboveHardCap && (
            <span className="ml-1">
              · Too large to plan in one pass (max 100). Split the day — e.g. plan one service zone
              or one half of the techs at a time.
            </span>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bd-cancel">
          Cancel
        </Button>
        <Button
          disabled={!canGenerate || generating || jobBuckets.unassigned.length === 0}
          onClick={handleGenerate}
          data-testid="bd-generate"
        >
          {generating ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Generating…</>
          ) : (
            <><Wand2 className="h-3.5 w-3.5 mr-1.5" /> Generate preview</>
          )}
        </Button>
      </div>
    </div>
  );

  // ── Render: Preview ──────────────────────────────────────────────────────
  const renderPreview = () => {
    if (!preview) return null;
    const s = preview.summary || {};
    return (
      <div className="space-y-4">
        <div
          className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs text-emerald-900"
          data-testid="bd-preview-summary"
        >
          <div className="font-medium">
            {s.plannedTaskCount} job{s.plannedTaskCount !== 1 ? 's' : ''} to assign · {s.assignedTaskCount} assigned ·{' '}
            {s.unassignableCount > 0 && (
              <span className="text-amber-700">{s.unassignableCount} could not be placed · </span>
            )}
            ~{s.estSavedMin} min saved vs simple order
          </div>
          <div className="text-[10px] text-emerald-700 mt-0.5">
            Total drive: ~{s.totalDriveMin} min · Locked appointments preserved: {s.lockedPreservedCount}
          </div>
        </div>

        {error && (
          <div
            className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start justify-between gap-2"
            data-testid="bd-preview-error"
          >
            <span className="flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              {error}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setStage('configure'); setError(null); }}
              data-testid="bd-preview-regenerate"
            >
              <RefreshCw className="h-3 w-3 mr-1" /> Regenerate
            </Button>
          </div>
        )}

        {/* Per-tech blocks */}
        <div className="space-y-3">
          {preview.assignments.map((a) => {
            const cap = a.capacity || {};
            return (
              <div
                key={a.techUid}
                className="border border-gray-200 rounded-lg p-3"
                data-testid={`bd-preview-tech-${a.techUid}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{a.techName}</div>
                    <div className="text-[10px] text-gray-500">{a.startAddress}</div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="text-gray-700">
                      {cap.used}/{cap.max} stops{cap.halfDay ? ' (½ day)' : ''}
                    </div>
                    <div className="text-gray-500">~{a.totalDriveMin} min drive</div>
                  </div>
                </div>
                {a.stops.length === 0 ? (
                  <div className="text-xs text-gray-400 italic px-2 py-3 text-center">
                    No jobs assigned
                  </div>
                ) : (
                  <ol className="space-y-1">
                    {a.stops.map((stop) => (
                      <li
                        key={stop.taskId}
                        className={`flex items-start gap-3 px-2 py-1.5 rounded text-xs ${
                          stop.wasPreviouslyAssignedTo &&
                          stop.wasPreviouslyAssignedTo !== a.techUid
                            ? 'bg-amber-50'
                            : 'bg-gray-50'
                        }`}
                        data-testid={`bd-stop-${stop.taskId}`}
                      >
                        <span className="text-gray-500 font-medium w-5 shrink-0">{stop.order}.</span>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">{stop.clientName}</div>
                          <div className="text-[10px] text-gray-500 truncate">{stop.address}</div>
                        </div>
                        <div className="text-right shrink-0 text-gray-500">
                          {stop.etaMin > 0 ? `+${stop.etaMin} min` : 'depot'}
                        </div>
                        {stop.wasPreviouslyAssignedTo &&
                          stop.wasPreviouslyAssignedTo !== a.techUid && (
                            <span className="text-[10px] text-amber-700 ml-1">moved</span>
                          )}
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>

        {/* Preserved locked */}
        {preview.preservedLocked.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-3" data-testid="bd-locked-section">
            <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Locked appointments (unchanged)
            </h4>
            <ul className="space-y-1">
              {preview.preservedLocked.map((l) => (
                <li
                  key={l.taskId}
                  className="text-xs text-gray-600 flex justify-between px-2 py-1 bg-gray-50 rounded"
                >
                  <span>{l.clientName}</span>
                  <span className="text-gray-400">
                    {l.scheduledStart ? new Date(l.scheduledStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Unassignable */}
        {preview.unassignable.length > 0 && (
          <div
            className="border border-amber-200 rounded-lg p-3 bg-amber-50"
            data-testid="bd-unassignable-section"
          >
            <h4 className="text-xs font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" />
              {preview.unassignable.length} job
              {preview.unassignable.length !== 1 ? 's' : ''} could not be placed
            </h4>
            <ul className="space-y-1">
              {preview.unassignable.map((u) => (
                <li
                  key={u.taskId}
                  className="text-xs flex justify-between items-center px-2 py-1"
                >
                  <span className="text-gray-800">{u.clientName}</span>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${REASON_TONE[u.reason] || REASON_TONE.geocode_failed}`}
                  >
                    {REASON_LABEL[u.reason] || u.reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={() => { setStage('configure'); setError(null); }}
            data-testid="bd-modify"
          >
            Modify
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bd-preview-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={applying || s.assignedTaskCount === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bd-apply"
          >
            {applying ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Applying…</>
            ) : (
              <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Apply plan</>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="build-day-sheet"
        onInteractOutside={(e) => {
          if (isInsidePacContainer(e)) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isInsidePacContainer(e)) e.preventDefault();
        }}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-indigo-600" />
            Build Day — {formatDateLong(dateKey)}
          </SheetTitle>
          <SheetDescription>
            {stage === 'configure' ? (
              <>Recommend technician assignments. You&apos;ll preview before anything changes.</>
            ) : (
              <>Review the proposed assignments below, then Apply or Modify.</>
            )}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4">{stage === 'configure' ? renderConfigure() : renderPreview()}</div>
        {/* Suppress unused-icon lint warnings — reserved for future variants */}
        {void ChevronRight}{void XCircle}
      </SheetContent>
    </Sheet>
  );
};

export default BuildDaySheet;

// ─── Source / Override pill ────────────────────────────────────────────────
// Shows the operator where a field's value came from:
//   • Override (amber)  — operator typed something different in this session
//   • Tech default      — saved per-technician default
//   • Company default   — using the company-wide fallback
//   • No default        — nothing saved anywhere; field starts blank/seed
function SourcePill({ source, overridden, testId }) {
  if (overridden) {
    return (
      <span
        className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800"
        data-testid={testId}
      >
        Override
      </span>
    );
  }
  if (source === 'tech') {
    return (
      <span
        className="text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600"
        data-testid={testId}
        title="From technician default"
      >
        Tech default
      </span>
    );
  }
  if (source === 'company') {
    return (
      <span
        className="text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border border-gray-200 bg-gray-50 text-gray-600"
        data-testid={testId}
        title="From company default"
      >
        Company default
      </span>
    );
  }
  return (
    <span
      className="text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-400"
      data-testid={testId}
      title="No saved default — type a value, then optionally Save as default"
    >
      No default
    </span>
  );
}
